"use client";

import GlobalForm from "@/components/forms/GlobalForm";
import Image from "next/image";
import React, { useState } from "react";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  PageName,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import useModalScrollLock from "@/hooks/useModalScrollLock";

interface AccountingRoadMapProps {
  title: React.ReactNode;
  subTitle?: string;
  images: string;
  para?: string;
  pageName: string;
  isCMAOverview?: boolean;
}

const Accounting_roadMap: React.FC<AccountingRoadMapProps> = ({
  title,
  subTitle,
  images,
  para,
  pageName,
  isCMAOverview,
}) => {
  const [isBrochureFormModelOpen, setIsBrochureFormModelOpen] =
    useState<boolean>(false);
  const [isFormModelOpen, setIsFormModelOpen] = useState<boolean>(false);

  const handleBrochureFormModelOpen = () => {
    sessionStorage.setItem("modal-open", "true");
    setIsBrochureFormModelOpen(true);
  };

  const handleFormModelOpen = () => {
    sessionStorage.setItem("modal-open", "true");
    setIsFormModelOpen(true);
  };

  const handleFormModelClose = () => {
    sessionStorage.removeItem("modal-open");
    setIsFormModelOpen(false);
  };

  const handleBrochureFormModelClose = () => {
    sessionStorage.removeItem("modal-open");
    setIsBrochureFormModelOpen(false);
  };

  useModalScrollLock(isBrochureFormModelOpen || isFormModelOpen, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  return (
    <>
      <div className="w-full bg-[#051A30]">
        <div className="main_container mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 lg:gap-8 2xl:gap-10">
            <div className="flex flex-col justify-center gap-3 lg:gap-6 py-8 lg:py-12">
              {isCMAOverview ? (
                <h2 className="text-[24px] md:text-[50px] lg:text-[50px] font-bold leading-[1.16] tracking-[-0.06em] text-[#FFBA08] max-w-[350px]">
                  Ready to{" "}
                  <a
                    href="/cma"
                    target="_blank"
                    className="text-[#39F] underline"
                  >
                    become a CMA?
                  </a>
                </h2>
              ) : (
                <h2 className="text-[24px] md:text-[48px] lg:text-[48px] font-bold leading-[1.2] tracking-[-0.04em] text-[#FFBA08] md:min-w-[470px] min-w-[200px]">
                  {title}
                </h2>
              )}

              {subTitle && (
                <h3 className="text-[18px] md:text-[30px] lg:text-[36px] font-normal text-white tracking-[0.01em]">
                  {subTitle}
                </h3>
              )}
              {para && (
                <p className="text-[14px] md:text-[18px] leading-5 md:leading-8 lg:text-[28px] font-normal text-white">
                  {para}
                </p>
              )}
            </div>
            <div className="pt-8 xl:pr-8">
              <Image
                src={`${images}?tr=w-180,h-245,q-95`}
                alt="Accounting Roadmap"
                className="w-full h-full rounded-lg shadow-lg"
                width={180}
                height={245}
                loading="lazy"
                sizes="(max-width: 1023px) calc(50vw - 24px), calc(33vw - 32px)"
              />
            </div>
            <div className="col-span-2 lg:col-span-1 flex flex-col items-center justify-center gap-6 lg:py-10 pb-8">
              <div className="flex lg:justify-end w-full">
                <div
                  className="relative w-full lg:max-w-[400px] p-4 border border-white rounded-2xl"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
                    boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
                    backdropFilter: "blur(28px)",
                  }}
                >
                  <Image
                    src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                    alt="star group"
                    width={34}
                    height={38}
                    className="absolute top-1 left-0"
                    loading="lazy"
                    sizes="34px"
                  />

                  <GlobalForm
                    course={pageName}
                    courseId={
                      pageName === "CPA" ? 1 : pageName === "CPA/CMA" ? 1 : 2
                    }
                    formHeading={
                      FormHeading.WEEKLY_LIVE_WEBINAR_WITH_VARUN_JAIN
                    }
                    formType="page"
                    isAStepperForm={true}
                    submitButtonName="Apply Now"
                    showSVG={false}
                    successMessage={SuccessMessageType.LIVE_WEBINAR}
                    darkBackground={true}
                  />
                </div>
              </div>
              <div className="flex flex-row gap-4 justify-center">
                <button
                  onClick={handleBrochureFormModelOpen}
                  className="px-3 xl:px-5 py-2 xl:py-[9px] border text-sm xl:text-base border-white text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0072ED]"
                >
                  Download Brochure
                </button>
                <button
                  onClick={handleFormModelOpen}
                  className="px-3 xl:px-5 py-2 xl:py-[9px] border border-white text-white font-semibold rounded-full hover:bg-[#0072ED] hover:text-white transition-colors duration-300"
                >
                  Speak to an Expert
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isBrochureFormModelOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[999] p-4">
          <div
            className="rounded-2xl shadow-lg relative h-auto mx-auto border border-white bg-[#263546] p-4 w-full max-w-[400px]"
            style={{
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleBrochureFormModelClose}
              className="text-white md:text-2xl text-xl absolute right-1 top-1 cursor-pointer"
            >
              ✕
            </button>
            <div>
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                alt="star group"
                width={34}
                height={38}
                className="absolute top-3 left-[-8px]"
                loading="lazy"
                sizes="34px"
              />
              <GlobalForm
                course={pageName === "CPA" ? "CPA" : "CMA"}
                courseId={pageName === "CPA" ? 1 : 2}
                formHeading={FormHeading.RECEIVE_PROGRAM_BROCHURE_VIA_EMAIL}
                formType="page"
                pageType="accounting page"
                subCategory={
                  pageName === "CPA"
                    ? FinanceAndAccountingSubcategory.CPA
                    : FinanceAndAccountingSubcategory.CMA
                }
                submitButtonName="Email Brochure"
                successMessage={SuccessMessageType.DOWNLOAD_BROCHURE}
                verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                darkBackground={true}
                brochureDownload={true}
                pageName={pageName === "CPA" ? PageName.CPA : PageName.CMA}
              />
            </div>
          </div>
        </div>
      )}

      {isFormModelOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-[auto] mx-auto border border-white p-4 w-[400px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleFormModelClose}
              className="text-white text-xl md:text-2xl absolute right-1 top-1 cursor-pointer"
            >
              ✕
            </button>
            <div>
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                alt="star group"
                width={34}
                height={38}
                className="absolute top-3 left-[-8px]"
                loading="lazy"
                sizes="34px"
              />
              <GlobalForm
                course={pageName === "CPA" ? "CPA" : "CMA"}
                courseId={pageName === "CPA" ? 1 : 2}
                formHeading={
                  FormHeading.SCHEDULE_A_VISIT_TO_MILES_OFFICE_OR_MEET_US_ONLINE
                }
                formType="page"
                isAStepperForm={true}
                pageType="accounting page"
                subCategory={
                  pageName === "CPA"
                    ? FinanceAndAccountingSubcategory.CPA
                    : FinanceAndAccountingSubcategory.CMA
                }
                submitButtonName="GET STARTED"
                successMessage={SuccessMessageType.ACCOUNTING}
                verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                darkBackground={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Accounting_roadMap;
